/**
 * TreatX Contact Inquiry Context
 * ================================
 * Phase 2B: Removed ALL localStorage, mock data, and frontend-only logic.
 * All inquiry data now flows through backend APIs:
 *   POST /api/inquiries              — submit (public)
 *   GET  /api/admin/inquiries         — list all (admin)
 *   PUT  /api/admin/inquiries/:id/status — update status
 *   POST /api/admin/inquiries/:id/respond — add response
 *   PUT  /api/admin/inquiries/:id/assign  — assign
 *   DELETE /api/admin/inquiries/:id       — delete
 *   GET  /api/admin/inquiries/stats       — stats
 */
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useAuthGate } from '../hooks/useAuthGate';
// The backend guards /api/admin/inquiries with the canonical `entity.view`
// permission, which the TreatX-side roles carry. The gate list must name the
// CANONICAL role identifiers — `treatx_super_admin` was missing, so a real
// platform admin never fetched the queue and the page showed 0 inquiries.
const _INQ_ROLES = [
  'admin', 'super_admin', 'platform_admin',
  'treatx_super_admin', 'treatx_admin', 'treatx_support',
  'treatx_coordinator', 'treatx_accountant',
];
import { api } from '../utils/api';

const ContactInquiryContext = createContext();

export const useContactInquiry = () => {
  const context = useContext(ContactInquiryContext);
  if (!context) {
    throw new Error('useContactInquiry must be used within ContactInquiryProvider');
  }
  return context;
};

export const INQUIRY_STATUS = {
  NEW: 'NEW',
  IN_PROGRESS: 'IN_PROGRESS',
  RESOLVED: 'RESOLVED',
};

export const INQUIRY_CATEGORIES = [
  { value: 'general', label: 'General Inquiry' },
  { value: 'patient', label: 'Patient Question' },
  { value: 'clinic', label: 'Clinic Registration' },
  { value: 'billing', label: 'Billing/Payments' },
  { value: 'technical', label: 'Technical Support' },
  { value: 'partnership', label: 'Partnership' },
  { value: 'complaint', label: 'Complaint' },
  // TREATX-DATA-PROTECTION-REQUEST-01 — GDPR / KVKK data-subject requests
  // are intake through the SAME public Contact flow.
  { value: 'privacy_data_request', label: 'Privacy / Data Protection Request' },
  { value: 'other', label: 'Other' },
];

// TREATX-DATA-PROTECTION-REQUEST-01 — server-mirrored upload constraints.
export const INQUIRY_ATTACHMENT_RULES = {
  maxFiles: 5,
  maxFileSizeBytes: 10 * 1024 * 1024,
  allowedExtensions: ['pdf', 'jpg', 'jpeg', 'png'],
};

// Normalize snake_case API response to camelCase
const normalizeInquiry = (i) => ({
  id: i.inquiry_id,
  inquiryId: i.inquiry_id,
  name: i.name,
  email: i.email,
  subject: i.subject,
  message: i.message,
  category: i.category,
  status: i.status,
  submittedAt: i.submitted_at,
  assignedTo: i.assigned_to,
  resolvedAt: i.resolved_at,
  resolvedBy: i.resolved_by,
  attachments: (i.attachments || []).map((a) => ({
    attachmentId: a.attachment_id,
    fileName: a.file_name,
    contentType: a.content_type,
    sizeBytes: a.size_bytes,
    uploadedAt: a.uploaded_at,
  })),
  responses: (i.responses || []).map((r) => ({
    id: r.response_id,
    text: r.text,
    by: r.by,
    timestamp: r.timestamp,
    delivery: r.delivery || null,
  })),
});

export const ContactInquiryProvider = ({ children }) => {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch all inquiries from backend
  const fetchInquiries = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/inquiries');
      const normalized = (data.inquiries || []).map(normalizeInquiry);
      setInquiries(normalized);
      return normalized;
    } catch (err) {
      console.error('[ContactInquiry] fetchInquiries error:', err);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // TREATX-BATCH-BOOT-01 — /api/admin/inquiries is TreatX-admin only.
  const { authorized: _inq_authorized } = useAuthGate({ roles: _INQ_ROLES });
  useEffect(() => {
    if (!_inq_authorized) return;
    fetchInquiries();
  }, [_inq_authorized, fetchInquiries]);

  // Submit a new inquiry (public). Optional attachments are uploaded in the
  // SAME request; the server only persists the inquiry once every file has
  // been validated and stored, so a resolved promise means both succeeded.
  //
  // TREATX-CONTACT-CSRF-MULTIPART-01 — sent through the canonical `api`
  // transport. The endpoint is public, but a visitor who happens to hold a
  // `treatx_auth` cookie (e.g. the Owner logged in as Super Admin) makes the
  // request a cookie-authenticated mutation, which the canonical CSRF
  // middleware enforces. Only the shared instance attaches the
  // double-submit `X-CSRF-Token` header from the `treatx_csrf` cookie.
  const submitInquiry = useCallback(async (formData, files = []) => {
    const payload = new FormData();
    payload.append('name', formData.name || '');
    payload.append('email', formData.email || '');
    payload.append('subject', formData.subject || '');
    payload.append('message', formData.message || '');
    payload.append('category', formData.category || 'general');
    (files || []).forEach((f) => payload.append('files', f, f.name));

    const { data } = await api.post('/inquiries/submit', payload);
    if (!data?.success) {
      throw new Error('Submission failed');
    }
    const newInq = normalizeInquiry(data.inquiry);
    setInquiries((prev) => [newInq, ...prev]);
    return newInq;
  }, []);

  const getAllInquiries = useCallback(() => inquiries, [inquiries]);

  const getInquiriesByStatus = useCallback(
    (status) => (status ? inquiries.filter((i) => i.status === status) : inquiries),
    [inquiries]
  );

  const getInquiryById = useCallback(
    (id) => inquiries.find((i) => i.id === id || i.inquiryId === id),
    [inquiries]
  );

  const getInquiriesByEmail = useCallback(
    (email) => inquiries.filter((i) => i.email.toLowerCase() === email.toLowerCase()),
    [inquiries]
  );

  // Update inquiry status via API
  const updateInquiryStatus = useCallback(async (inquiryId, newStatus) => {
    try {
      const { data } = await api.put(`/admin/inquiries/${inquiryId}/status`, {
        status: newStatus,
      });
      if (data.success) {
        const updated = normalizeInquiry(data.inquiry);
        setInquiries((prev) => prev.map((i) => (i.id === inquiryId ? updated : i)));
      }
    } catch (err) {
      console.error('[ContactInquiry] updateInquiryStatus error:', err);
    }
  }, []);

  // Assign inquiry via API
  const assignInquiry = useCallback(async (inquiryId, adminId) => {
    try {
      const { data } = await api.put(`/admin/inquiries/${inquiryId}/assign`, {
        assigned_to: adminId,
      });
      if (data.success) {
        const updated = normalizeInquiry(data.inquiry);
        setInquiries((prev) => prev.map((i) => (i.id === inquiryId ? updated : i)));
      }
    } catch (err) {
      console.error('[ContactInquiry] assignInquiry error:', err);
    }
  }, []);

  // Add response via API. Returns the truthful per-channel delivery result
  // so the caller can show what actually happened (TREATX-INQUIRY-REPLY-DELIVERY-01).
  const addResponse = useCallback(async (inquiryId, responseText, adminId = 'TreatX Admin', retryResponseId = null, resendUncertain = false) => {
    // Snapshot the response IDs that existed BEFORE sending. If the answer is
    // lost, only an ID that was not in this set can be the new reply — an
    // older response with identical wording must never be mistaken for it.
    const idsBefore = new Set(
      ((inquiries.find((i) => i.id === inquiryId) || {}).responses || [])
        .map((r) => r.id)
        .filter(Boolean),
    );
    try {
      const { data } = await api.post(`/admin/inquiries/${inquiryId}/respond`, {
        text: responseText,
        by: adminId,
        retry_response_id: retryResponseId,
        resend_uncertain: resendUncertain,
      });
      // Refetch so the history and delivery badges reflect the server.
      await fetchInquiries();
      return data;
    } catch (err) {
      console.error('[ContactInquiry] addResponse error:', err);
      // A lost response does NOT prove either channel failed — the reply may
      // be persisted and even delivered. Never fabricate `failed` here.
      // Reconcile against the stored record using the existing fetch path.
      const status = err?.response?.status;
      let reconciled = null;
      try {
        const fresh = await fetchInquiries();
        const row = (fresh || []).find((i) => i.id === inquiryId);
        if (row && retryResponseId) {
          // A retry never creates a record: match the exact response_id.
          reconciled = (row.responses || []).find((r) => r.id === retryResponseId) || null;
        } else if (row && responseText) {
          const key = (responseText || '').trim().replace(/\s+/g, ' ');
          reconciled = (row.responses || []).find(
            (r) => !idsBefore.has(r.id)
              && (r.text || '').trim().replace(/\s+/g, ' ') === key,
          ) || null;
        }
      } catch (reconErr) {
        console.error('[ContactInquiry] reconciliation failed:', reconErr);
      }

      if (reconciled) {
        // The reply IS stored. Report its real delivery state.
        return {
          success: false,
          reconciled: true,
          response: reconciled,
          delivery: reconciled.delivery || {},
        };
      }
      if (status && status >= 400 && status < 500) {
        // The server answered and rejected the request: nothing was stored.
        return { success: false, rejected: true, httpStatus: status, delivery: {} };
      }
      // Genuinely unknown: no answer, and no matching record found.
      return { success: false, unknown: true, httpStatus: status || null, delivery: {} };
    }
  }, [fetchInquiries, inquiries]);

  // Resolve inquiry
  const resolveInquiry = useCallback(
    async (inquiryId) => updateInquiryStatus(inquiryId, INQUIRY_STATUS.RESOLVED),
    [updateInquiryStatus]
  );

  // Delete inquiry via API
  const deleteInquiry = useCallback(async (inquiryId) => {
    try {
      const { data } = await api.delete(`/admin/inquiries/${inquiryId}`);
      if (data.success) {
        setInquiries((prev) => prev.filter((i) => i.id !== inquiryId));
      }
    } catch (err) {
      console.error('[ContactInquiry] deleteInquiry error:', err);
    }
  }, []);

  // Compute stats from loaded data
  const getStats = useCallback(() => {
    const newCount = inquiries.filter((i) => i.status === INQUIRY_STATUS.NEW).length;
    const inProgressCount = inquiries.filter((i) => i.status === INQUIRY_STATUS.IN_PROGRESS).length;
    const resolvedCount = inquiries.filter((i) => i.status === INQUIRY_STATUS.RESOLVED).length;

    const resolvedInquiries = inquiries.filter((i) => i.status === INQUIRY_STATUS.RESOLVED && i.resolvedAt);
    let avgResponseTime = null;
    if (resolvedInquiries.length > 0) {
      const totalTime = resolvedInquiries.reduce((acc, inq) => {
        const submitted = new Date(inq.submittedAt).getTime();
        const resolved = new Date(inq.resolvedAt).getTime();
        return acc + (resolved - submitted);
      }, 0);
      avgResponseTime = totalTime / resolvedInquiries.length;
    }

    return {
      total: inquiries.length,
      new: newCount,
      inProgress: inProgressCount,
      resolved: resolvedCount,
      avgResponseTime,
    };
  }, [inquiries]);

  const value = {
    inquiries,
    loading,
    fetchInquiries,
    submitInquiry,
    getAllInquiries,
    getInquiriesByStatus,
    getInquiryById,
    getInquiriesByEmail,
    getStats,
    updateInquiryStatus,
    assignInquiry,
    addResponse,
    resolveInquiry,
    deleteInquiry,
    INQUIRY_STATUS,
    INQUIRY_CATEGORIES,
    INQUIRY_ATTACHMENT_RULES,
  };

  return (
    <ContactInquiryContext.Provider value={value}>
      {children}
    </ContactInquiryContext.Provider>
  );
};

export default ContactInquiryContext;
